/**
 * Consentimento de cookies / armazenamento (LGPD — Lei 13.709/2018).
 *
 * Categorias:
 *  - essenciais: sempre ativas (tema, escolha de consentimento, formulários).
 *  - analytics: medição de audiência. Só carrega depois do "sim" do visitante.
 *  - thirdParty: conteúdo de terceiros (mapas do Google). Só carrega depois do "sim".
 *
 * Regras seguidas: nada não essencial antes do consentimento; recusar é tão fácil
 * quanto aceitar; a escolha pode ser mudada a qualquer momento (link no rodapé);
 * a escolha vale 12 meses e é pedida de novo se a versão mudar.
 */

/** Preencher com o ID real do Google Analytics 4 (formato G-XXXXXXXXXX). Vazio = análise desligada. */
export const GA_MEASUREMENT_ID = '';

export const CONSENT_KEY = 'stib_palestra_consent_v1';
export const CONSENT_VERSION = 1;
export const CONSENT_EVENT = 'stib:consent-changed';
export const OPEN_PREFERENCES_EVENT = 'stib:open-consent-preferences';
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export interface ConsentState {
  v: number;
  analytics: boolean;
  thirdParty: boolean;
  ts: number;
}

type GtagFn = (...args: unknown[]) => void;
const win = (): { gtag?: GtagFn } => window as unknown as { gtag?: GtagFn };

export function readConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as ConsentState;
    if (!s || s.v !== CONSENT_VERSION || typeof s.ts !== 'number') return null;
    if (Date.now() - s.ts > MAX_AGE_MS) return null;
    return { v: s.v, analytics: !!s.analytics, thirdParty: !!s.thirdParty, ts: s.ts };
  } catch {
    return null;
  }
}

let analyticsLoaded = false;

function loadAnalytics(): void {
  if (analyticsLoaded || !GA_MEASUREMENT_ID) return;
  analyticsLoaded = true;
  const g = win().gtag;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(GA_MEASUREMENT_ID);
  document.head.appendChild(s);
  if (typeof g === 'function') {
    g('js', new Date());
    g('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
  }
}

/** Remove cookies de medição (_ga*) quando o visitante retira o consentimento. */
function clearAnalyticsStorage(): void {
  try {
    const host = location.hostname;
    const parts = host.split('.');
    const domains = ['', host, '.' + host];
    if (parts.length > 2) domains.push('.' + parts.slice(-3).join('.'));
    domains.push('.' + parts.slice(-2).join('.'));
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (name === '_ga' || name.indexOf('_ga_') === 0 || name === '_gid') {
        domains.forEach((d) => {
          document.cookie =
            name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + (d ? '; domain=' + d : '');
        });
      }
    });
  } catch {
    /* nunca travar a interface por causa de limpeza */
  }
}

export function applyConsent(s: ConsentState): void {
  try {
    const g = win().gtag;
    if (typeof g === 'function') {
      g('consent', 'update', {
        analytics_storage: s.analytics ? 'granted' : 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
    if (s.analytics) loadAnalytics();
    else clearAnalyticsStorage();
  } catch {
    /* silencioso */
  }
}

export function saveConsent(analytics: boolean, thirdParty: boolean): ConsentState {
  const s: ConsentState = { v: CONSENT_VERSION, analytics, thirdParty, ts: Date.now() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(s));
  } catch {
    /* navegação privada: vale só nesta visita */
  }
  applyConsent(s);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: s }));
  return s;
}

export function openConsentPreferences(): void {
  window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
}
